import { createReducer } from '@reduxjs/toolkit';
import { BatchDto, GoodsDto } from '../../api/ClientApi';

import {
    saveGoods,
    importAllGoods,
    updateBatchForGood,
    addOrUpdateReduxGoods,
    removeGoodsFromRedux,
    addOrUpdateMultipleReduxGoods,
} from '../actions/goodsActions';

export interface GoodsState {
    goods: GoodsDto[] | null;
    // True while /api/Goods/all is in flight; importAllGoods' condition uses it so several
    // components mounting at once (OrdersList + OrdersTable + a form) trigger one request.
    goodsFetchActive: boolean;
}

export const goodsReducer = createReducer<GoodsState>({ goods: null, goodsFetchActive: false }, (builder) => {
    builder
        .addCase(saveGoods.fulfilled, (state: GoodsState, action: any) => {
            if (state.goods === null) {
                return;
            }
            if (!!action.payload) {
                const newGoods = [...state.goods, ...action.payload];

                state.goods = newGoods.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
            }
        })
        .addCase(importAllGoods.pending, (state: GoodsState) => {
            state.goodsFetchActive = true;
        })
        .addCase(importAllGoods.rejected, (state: GoodsState) => {
            state.goodsFetchActive = false;
        })
        .addCase(importAllGoods.fulfilled, (state: GoodsState, action: any) => {
            const newGoods = action.payload as GoodsDto[];

            state.goods = newGoods.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
            state.goodsFetchActive = false;
        })
        .addCase(updateBatchForGood, (state: GoodsState, action: any) => {
            const batch = action.payload as BatchDto;
            if (!!batch) {
                const currGoods = state.goods?.filter((g) => g.batch?.id === batch.id);

                if (!!currGoods && currGoods.length > 0) {
                    currGoods.map((currGood) => {
                        const currGoodIndex = state.goods?.findIndex((g) => g.id === currGood.id);

                        if (currGoodIndex !== undefined && !!state.goods) {
                            const newGood = new GoodsDto(state.goods[currGoodIndex]);
                            newGood.batch = batch;
                            state.goods = [
                                ...state.goods?.slice(0, currGoodIndex),
                                newGood,
                                ...state.goods.slice(currGoodIndex + 1),
                            ];
                        }
                    });
                }
            }
        })
        .addCase(addOrUpdateReduxGoods, (state: GoodsState, action: any) => {
            const newGoods = action.payload as GoodsDto;

            if (!!newGoods) {
                const oldGoods = state.goods?.find((g) => g.id === newGoods.id);

                if (!oldGoods) {
                    if (!state.goods) {
                        state.goods = [newGoods];
                    } else {
                        state.goods.unshift(newGoods);
                    }
                } else {
                    const oldGoodsIndex = state.goods?.findIndex((g) => g.id === oldGoods.id);

                    if (oldGoodsIndex !== undefined && oldGoodsIndex > -1) {
                        if (!!state.goods) {
                            state.goods.splice(oldGoodsIndex, 1, newGoods);
                        } else {
                            state.goods = [newGoods];
                        }
                    }
                }
            }
        })
        .addCase(addOrUpdateMultipleReduxGoods, (state: GoodsState, action: any) => {
            const newGoodsList = action.payload as GoodsDto[];

            const oldGoodsList = state.goods ? [...state.goods] : [];

            newGoodsList.map((newGoods) => {
                if (!!newGoods) {
                    const oldGoods = oldGoodsList?.find((g) => g.id === newGoods.id);

                    if (!oldGoods) {
                        oldGoodsList.unshift(newGoods);
                    } else {
                        const oldGoodsIndex = oldGoodsList.findIndex((g) => g.id === oldGoods.id);

                        oldGoodsList.splice(oldGoodsIndex, 1, newGoods);
                    }
                }
            });

            state.goods = oldGoodsList;
        })
        .addCase(removeGoodsFromRedux, (state: GoodsState, action: any) => {
            const oldGoodsId = action.payload as number;

            if (!!oldGoodsId) {
                const oldGoodsIndex = state.goods?.findIndex((g) => g.id === oldGoodsId);

                if (oldGoodsIndex !== undefined && oldGoodsIndex > -1) {
                    if (!!state.goods) {
                        state.goods.splice(oldGoodsIndex, 1);
                    } else {
                        state.goods = [];
                    }
                }
            }
        });
});
